import React, { useEffect, useRef, useState } from 'react';

import minimapRestIcon from '../../assets/minimap/1_Rest_Site.png';
import minimapChestIcon from '../../assets/minimap/2_Chest.png';
import minimapShopIcon from '../../assets/minimap/3_Shop.png';
import minimapWellIcon from '../../assets/minimap/4_Well.png';
import minimapEnemyIcon from '../../assets/minimap/5_Enemy.png';
import minimapShrineIcon from '../../assets/minimap/6_Shrine.png';
import minimapStorylineIcon from '../../assets/minimap/7_Storyline.png';
import minimapTrapIcon from '../../assets/minimap/8_Trap.png';
import minimapStepsDownIcon from '../../assets/minimap/9_Steps_Down.png';
import minimapStepsUpIcon from '../../assets/minimap/10_Steps_Up.png';
import minimapBrazierIcon from '../../assets/minimap/11_Brazier.png';
import minimapBrazierOutIcon from '../../assets/minimap/11_Brazier_out.png';
import minimapDoorLockedIcon from '../../assets/minimap/12_Door_Locked.png';
import minimapDoorUnlockedIcon from '../../assets/minimap/13_Door_Unlocked.png';
import minimapLoreIcon from '../../assets/minimap/14_Lore.png';
import minimapEnemyBossIcon from '../../assets/minimap/15_Enemy_Boss.png';
import minimapArrowUpIcon from '../../assets/minimap_arrow_up.png';
import minimapDecoration1 from '../../assets/minimap_decoration1.png';
import minimapDecoration2 from '../../assets/minimap_decoration2.png';
import minimapDecoration3 from '../../assets/minimap_decoration3.png';
import minimapDecoration4 from '../../assets/minimap_decoration4.png';

import { BRAZIER_LIGHTING_ENABLED } from '../../game/lighting/brazierLightingConfig';

function MiniMapArrowIcon({ rotate = 0 }) {
  return (
    <img
      src={minimapArrowUpIcon}
      alt=""
      aria-hidden="true"
      draggable={false}
      loading="lazy"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        transform: `rotate(${rotate}deg)`,
        transformOrigin: 'center',
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserDrag: 'none',
        filter:
          'drop-shadow(0 1px 0 rgba(0,0,0,0.55)) drop-shadow(0 0 2px rgba(0,0,0,0.35))',
      }}
    />
  );
}

function EdgeDecoration({ side }) {
  const baseStyle = {
    position: 'absolute',
    top: '50%',
    width: 28,
    height: '36vh',
    minHeight: 220,
    maxHeight: 320,
    objectFit: 'contain',
    opacity: 0.92,
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserDrag: 'none',
    transform: 'translateY(-50%)',
  };

  if (side === 'left') {
    return <img src={minimapDecoration2} alt="" draggable={false} loading="lazy" style={{ ...baseStyle, left: 12 }} />;
  }

  return (
    <img
      src={minimapDecoration2}
      alt=""
      draggable={false}
      loading="lazy"
      style={{
        ...baseStyle,
        right: 12,
        transform: 'translateY(-50%) scaleX(-1)',
      }}
    />
  );
}

function CornerDecoration({ corner }) {
  const rotations = {
    tl: 'rotate(0deg)',
    tr: 'rotate(90deg)',
    br: 'rotate(180deg)',
    bl: 'rotate(270deg)',
  };

  const position = {
    tl: { top: 22, left: 22 },
    tr: { top: 22, right: 22 },
    br: { bottom: 22, right: 22 },
    bl: { bottom: 22, left: 22 },
  };

  return (
    <img
      src={minimapDecoration3}
      alt=""
      aria-hidden="true"
      draggable={false}
      loading="lazy"
      style={{
        position: 'absolute',
        width: 68,
        height: 68,
        objectFit: 'contain',
        opacity: 0.94,
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserDrag: 'none',
        transform: rotations[corner],
        transformOrigin: 'center',
        ...position[corner],
      }}
    />
  );
}


function CloseCornerButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close map"
      title="Close map"
      style={{
        position: 'absolute',
        top: 22,
        right: 22,
        width: 68,
        height: 68,
        padding: 0,
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        display: 'block',
        cursor: 'pointer',
        zIndex: 42,
        pointerEvents: 'auto',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <img
        src={minimapDecoration4}
        alt=""
        aria-hidden="true"
        draggable={false}
        loading="lazy"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          opacity: 0.96,
          pointerEvents: 'none',
          userSelect: 'none',
          WebkitUserDrag: 'none',
          filter: 'drop-shadow(0 1px 0 rgba(255,240,210,0.06)) drop-shadow(0 2px 6px rgba(0,0,0,0.22))',
        }}
      />
    </button>
  );
}

export default function MiniMapOverlay({
  open,
  onClose,
  map,
  exploredTiles,
  visibleTiles,
  playerPos,
  floorLevel,
  currentFloorMood,
  litBraziers,
}) {
  const scrollerRef = useRef(null);
  const playerTileRef = useRef(null);
  const [edgePad, setEdgePad] = useState({ x: 14, y: 14 });

  const CELL_W = 30;
  const CELL_H = 46;
  const GAP = 4;

  const PAN_STEP_X = (CELL_W + GAP) * 5;
  const PAN_STEP_Y = (CELL_H + GAP) * 4;

  const panMiniMapBy = (dxPx, dyPx) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);

    const nextLeft = Math.max(0, Math.min(scroller.scrollLeft + dxPx, maxLeft));
    const nextTop = Math.max(0, Math.min(scroller.scrollTop + dyPx, maxTop));

    try {
      scroller.scrollTo({ left: nextLeft, top: nextTop, behavior: 'smooth' });
    } catch {
      scroller.scrollLeft = nextLeft;
      scroller.scrollTop = nextTop;
    }
  };

  const PARCH_BASE =
    'radial-gradient(ellipse at 50% 28%, rgba(255,244,219,0.97) 0%, rgba(231,194,126,0.97) 46%, rgba(184,131,69,0.97) 100%)';
  const PARCH_STAIN_1 =
    'radial-gradient(circle at 18% 22%, rgba(20,12,8,0.08) 0%, rgba(20,12,8,0) 58%)';
  const PARCH_STAIN_2 =
    'radial-gradient(circle at 76% 64%, rgba(20,12,8,0.08) 0%, rgba(20,12,8,0) 62%)';
  const PARCH_EDGE_BURN =
    'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 56%, rgba(0,0,0,0.14) 86%, rgba(0,0,0,0.26) 100%)';
  const PARCH_GRAIN =
    'repeating-linear-gradient(0deg, rgba(0,0,0,0.018) 0px, rgba(0,0,0,0.018) 2px, rgba(255,255,255,0.012) 3px, rgba(255,255,255,0.012) 6px)';
  const PARCH_FOLD_V =
    'linear-gradient(90deg, rgba(0,0,0,0) 49.2%, rgba(88,62,32,0.14) 50%, rgba(255,245,220,0.12) 50.8%, rgba(0,0,0,0) 52%)';
  const PARCH_FOLD_H =
    'linear-gradient(180deg, rgba(0,0,0,0) 49.35%, rgba(88,62,32,0.08) 50%, rgba(255,245,220,0.07) 50.75%, rgba(0,0,0,0) 52%)';

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const updatePad = () => {
      const padX = Math.max(14, Math.floor(scroller.clientWidth / 2 - CELL_W / 2));
      const padY = Math.max(14, Math.floor(scroller.clientHeight / 2 - CELL_H / 2));
      setEdgePad({ x: padX, y: padY });
    };

    updatePad();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updatePad);
      ro.observe(scroller);
    } else {
      window.addEventListener('resize', updatePad);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', updatePad);
    };
  }, [open, CELL_W, CELL_H]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const centerPlayer = () => {
      const scroller = scrollerRef.current;
      const tile = playerTileRef.current;
      if (!scroller || !tile) return false;

      const sRect = scroller.getBoundingClientRect();
      const tRect = tile.getBoundingClientRect();

      const tileLeftInScroller = (tRect.left - sRect.left) + scroller.scrollLeft;
      const tileTopInScroller = (tRect.top - sRect.top) + scroller.scrollTop;

      const targetLeft =
        tileLeftInScroller - (scroller.clientWidth / 2) + (tRect.width / 2);
      const targetTop =
        tileTopInScroller - (scroller.clientHeight / 2) + (tRect.height / 2);

      const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);

      scroller.scrollLeft = Math.max(0, Math.min(targetLeft, maxLeft));
      scroller.scrollTop = Math.max(0, Math.min(targetTop, maxTop));

      return true;
    };

    const tryCenter = (attempt = 0) => {
      if (cancelled) return;

      const ok = centerPlayer();
      if (ok) return;

      if (attempt < 6) {
        requestAnimationFrame(() => tryCenter(attempt + 1));
      }
    };

    requestAnimationFrame(() => tryCenter(0));

    return () => {
      cancelled = true;
    };
  }, [open, playerPos.x, playerPos.y, exploredTiles, edgePad.x, edgePad.y]);

  if (!open) return null;

  const W = map?.[0]?.length ?? 0;

  const isExplored = (x, y) =>
    exploredTiles?.has?.(`${x},${y}`) || (x === playerPos.x && y === playerPos.y);

  const isCurrentlyVisible = (x, y) => visibleTiles?.has?.(`${x},${y}`);

  const isWalkableMini = (t) => {
    if (!t) return false;
    if (t === 'non-walkable') return false;
    if (typeof t === 'string' && t.startsWith('statue-')) return false;
    return true;
  };

  const IconWrap = ({ src, alt = '', filter = '' }) => (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        pointerEvents: 'none',
        zIndex: 3,
        opacity: 0.98,
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          width: '72%',
          height: '72%',
          objectFit: 'contain',
          display: 'block',
          userSelect: 'none',
          filter: `${filter} drop-shadow(0 1px 0 rgba(0,0,0,0.55)) drop-shadow(0 0 2px rgba(0,0,0,0.35))`,
        }}
      />
    </div>
  );

  const tileIconSrc = (tile, x, y) => {
    if (!tile) return null;
    if (tile === 'enemy-boss') return minimapEnemyBossIcon;
    if (tile === 'enemy') return minimapEnemyIcon;
    if (tile === 'rest') return minimapRestIcon;
    if (tile === 'steps-down') return minimapStepsDownIcon;
    if (tile === 'steps-up') return minimapStepsUpIcon;
    if (tile === 'merchant') return minimapShopIcon;
    if (tile === 'brazier') {
      const lit =
        !BRAZIER_LIGHTING_ENABLED || (litBraziers && litBraziers.has(`${x},${y}`));
      return lit ? minimapBrazierIcon : minimapBrazierOutIcon;
    }
    if (tile === 'trap') return minimapTrapIcon;
    if (tile === 'storyline') return minimapStorylineIcon;
    if (tile === 'shrine') return minimapShrineIcon;
    if (tile === 'well') return minimapWellIcon;
    if (tile === 'door-locked') return minimapDoorLockedIcon;
    if (tile === 'door-open') return minimapDoorUnlockedIcon;
    if (tile === 'lore') return minimapLoreIcon;
    if (tile.startsWith('chest-')) return minimapChestIcon;
    if (tile.startsWith('bookshelf-')) return minimapLoreIcon;
    return null;
  };

  const tileIconFilter = (tile) => {
    if (tile === 'steps-down') {
      return 'brightness(0) saturate(100%) invert(18%) sepia(86%) saturate(2475%) hue-rotate(262deg) brightness(93%) contrast(98%)';
    }

    if (tile === 'trap') {
      return 'brightness(0) saturate(100%) invert(18%) sepia(98%) saturate(5227%) hue-rotate(355deg) brightness(101%) contrast(116%)';
    }

    if (
      tile === 'chest-locked' ||
      tile === 'chest-closed' ||
      tile === 'chest-simple-locked' ||
      tile === 'chest-simple-closed' ||
      tile === 'chest-rare-locked' ||
      tile === 'chest-rare-closed'
    ) {
      return 'brightness(0) saturate(100%) invert(73%) sepia(76%) saturate(862%) hue-rotate(357deg) brightness(97%) contrast(96%)';
    }

    return '';
  };

  const tileIcon = (tile, x, y) => {
    const src = tileIconSrc(tile, x, y);
    if (!src) return null;
    return <IconWrap src={src} alt="" filter={tileIconFilter(tile)} />;
  };

  const tileStyleFor = (t, x, y) => {
    if (!isExplored(x, y)) return null;
    if (!isWalkableMini(t)) return null;

    const visBoost = isCurrentlyVisible(x, y) ? 0.12 : 0;
    const aTop = Math.min(0.95, 0.78 + visBoost);
    const aBot = Math.min(0.98, 0.90 + visBoost);

    return {
      background: `linear-gradient(180deg, rgba(0,0,0,${aTop}) 0%, rgba(0,0,0,${aBot}) 100%)`,
      border: '1px solid rgba(0,0,0,0.34)',
      borderRadius: 4,
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.32), 0 1px 0 rgba(0,0,0,0.15)',
    };
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{
        background: `${PARCH_EDGE_BURN}, ${PARCH_STAIN_1}, ${PARCH_STAIN_2}, ${PARCH_FOLD_V}, ${PARCH_FOLD_H}, ${PARCH_GRAIN}, ${PARCH_BASE}`,
        backgroundBlendMode: 'multiply, multiply, multiply, multiply, overlay, overlay, normal',
        boxShadow: 'inset 0 0 120px rgba(0,0,0,0.18)',
        touchAction: 'none',
      }}
    >
      <CornerDecoration corner="tl" />
      <CloseCornerButton onClick={onClose} />
      <CornerDecoration corner="br" />
      <CornerDecoration corner="bl" />
      <EdgeDecoration side="left" />
      <EdgeDecoration side="right" />

      <div
        className="relative w-full h-full overflow-hidden flex flex-col"
        style={{ background: 'transparent' }}
      >
        <div
          data-ui="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 35,
          }}
        >
          <button
            type="button"
            data-ui="true"
            onClick={panMiniMapBy(-PAN_STEP_X, 0)}
            aria-label="Pan minimap left"
            title="Pan left"
            style={{
              position: 'absolute',
              left: 2,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 58,
              height: 58,
              padding: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <MiniMapArrowIcon rotate={-90} />
          </button>

          <button
            type="button"
            data-ui="true"
            onClick={panMiniMapBy(PAN_STEP_X, 0)}
            aria-label="Pan minimap right"
            title="Pan right"
            style={{
              position: 'absolute',
              right: 2,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 58,
              height: 58,
              padding: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <MiniMapArrowIcon rotate={90} />
          </button>
        </div>

        <div data-ui="true" className="px-4 pt-5 pb-1 text-center relative shrink-0" style={{ zIndex: 20 }}>
          <button
            type="button"
            data-ui="true"
            onClick={panMiniMapBy(0, -PAN_STEP_Y)}
            aria-label="Pan minimap up"
            title="Pan up"
            style={{
              width: 54,
              height: 54,
              margin: '0 auto 2px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'auto',
              touchAction: 'manipulation',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <MiniMapArrowIcon rotate={0} />
          </button>

          <div
            className="font-serif leading-none"
            style={{
              fontSize: 'clamp(22px, 2.7vw, 36px)',
              color: 'rgba(20,14,10,0.96)',
              textShadow: '0 1px 0 rgba(255,245,220,0.28)',
            }}
          >
            Floor {floorLevel}
          </div>

          <div
            className="font-karma"
            style={{
              fontSize: 'clamp(12px, 1.05vw, 17px)',
              lineHeight: 1.05,
              color: 'rgba(35,25,18,0.88)',
              marginTop: 2,
            }}
          >
            {currentFloorMood?.name ?? currentFloorMood ?? 'The Dungeon Map'}
          </div>

          <img
            src={minimapDecoration1}
            alt=""
            aria-hidden="true"
            draggable={false}
            style={{
              width: 'min(188px, 16vw)',
              minWidth: 122,
              height: 'auto',
              margin: '8px auto 0',
              display: 'block',
              opacity: 0.95,
              pointerEvents: 'none',
              userSelect: 'none',
              WebkitUserDrag: 'none',
            }}
          />
        </div>

        <div className="px-12 pb-4 pt-1 flex-1 min-h-0">
          <div
            className="w-full h-full"
            style={{
              position: 'relative',
              background: 'transparent',
            }}
          >
            <div
              data-ui="true"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 30,
              }}
            >
              <button
                type="button"
                data-ui="true"
                onClick={panMiniMapBy(0, PAN_STEP_Y)}
                aria-label="Pan minimap down"
                title="Pan down"
                style={{
                  position: 'absolute',
                  bottom: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 62,
                  height: 62,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  boxShadow: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  display: 'grid',
                  placeItems: 'center',
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  userSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <MiniMapArrowIcon rotate={180} />
              </button>
            </div>

            <div
              ref={scrollerRef}
              className="mx-auto"
              style={{
                width: '100%',
                height: '100%',
                maxHeight: 'none',
                overflow: 'auto',
                padding: 0,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
                pointerEvents: 'none',
                touchAction: 'none',
              }}
            >
              <div
                style={{
                  paddingLeft: edgePad.x,
                  paddingRight: edgePad.x,
                  paddingTop: edgePad.y,
                  paddingBottom: edgePad.y,
                  width: 'max-content',
                  minWidth: '100%',
                  minHeight: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${W}, ${CELL_W}px)`,
                    gap: GAP,
                    justifyContent: 'start',
                  }}
                >
                  {map.map((row, y) =>
                    row.map((t, x) => {
                      const isPlayer = x === playerPos.x && y === playerPos.y;
                      const walkable = isWalkableMini(t);
                      const explored = isExplored(x, y);
                      const show = walkable && (explored || isPlayer);

                      if (!show) {
                        return (
                          <div
                            key={`${x},${y}`}
                            style={{ width: CELL_W, height: CELL_H }}
                          />
                        );
                      }

                      const st = tileStyleFor(t, x, y);
                      const icon = tileIcon(t, x, y);

                      return (
                        <div
                          key={`${x},${y}`}
                          ref={isPlayer ? playerTileRef : null}
                          style={{
                            width: CELL_W,
                            height: CELL_H,
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              ...(st || {}),
                              position: 'relative',
                              transform: isPlayer ? 'scale(1.35)' : 'scale(1)',
                              transformOrigin: 'center',
                              zIndex: isPlayer ? 5 : 1,
                              boxShadow: isPlayer
                                ? '0 0 0 2px rgba(255,210,120,0.55), 0 0 22px rgba(255,170,70,0.22), 0 0 18px rgba(0,0,0,0.35)'
                                : st
                                  ? st.boxShadow
                                  : 'none',
                            }}
                          >
                            {isPlayer && (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background:
                                    'linear-gradient(180deg, rgba(255,220,140,0.22), rgba(255,160,70,0.10))',
                                  boxShadow:
                                    'inset 0 0 0 1px rgba(255,220,140,0.55)',
                                  pointerEvents: 'none',
                                  zIndex: 2,
                                }}
                              />
                            )}

                            {icon}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
